<template>
  <div class="row">
    <div :class="columnsClass">
      <LabelAndTooltip
        :show-tooltip="cprLabel !== null"
        :label="cprLabel"
        :tooltip="cprTooltip"
        v-if="cprLabel !== null"
      />
      <Field
        :name="'cpr' + name"
        :id="'cpr' + name"
        :key="'cpr' + name"
        v-model="formattedCprNo"
        :rules="{
          required: !repeatCprNumber && required,
          cpr_required: repeatCprNumber && required,
          cpr_number_field: required,
          mod11: required
        }"
        :label="label"
        :placeholder="placeholder"
        type="tel"
        data-test="input-cpr"
        data-test-full-cpr
        autocomplete="off"
        maxlength="11"
        :class="{ invalid: errors !== null && errors['cpr' + name] !== undefined }"
      />
    </div>
    <div v-if="repeatCprNumber" :class="columnsClass">
      <div data-test="label-cpr-repeat" v-html="repeatCprLabelComputed" />
      <Field
        :name="'repeatCpr' + name"
        :id="'repeatCpr' + name"
        :key="'repeatCpr' + name"
        v-model="formattedRepeatedCprNo"
        :rules="'repated_required|cpr_number_repeat:' + 'cpr' + name"
        :label="label"
        type="tel"
        data-test="input-cpr-repeat"
        autocomplete="off"
        maxlength="11"
        :class="{ invalid: errors !== null && errors['repeatCpr' + name] !== undefined }"
      />
    </div>
    <div class="small-12 columns">
      <ValidationError
        :name="'cpr' + name"
        :scope="scope"
        data-test="validation-cpr"
        data-test-error="full-cpr"
      />
      <ValidationError
        v-if="firstFieldValid"
        :name="'repeatCpr' + name"
        data-test-error="repeatCpr"
      />
    </div>
  </div>
</template>

<script>
import { defineRule } from 'vee-validate';
import messages from '@/vendor/da_messages';

export default {
  name: 'CprNumberField',

  props: {
    modelValue: {
      type: String,
      default() {
        return '';
      }
    },
    scope: {
      type: String,
      default() {
        return null;
      }
    },
    cprLabel: {
      type: String,
      default() {
        return null;
      }
    },
    cprTooltip: {
      type: String,
      default() {
        return null;
      }
    },
    label: {
      type: String,
      default() {
        return null;
      }
    },
    required: {
      type: Boolean,
      default() {
        return true;
      }
    },
    name: {
      type: String,
      default() {
        return 'full-cpr';
      }
    },
    repeatCprNumber: {
      type: Boolean,
      default() {
        return false;
      }
    },
    repeatCprLabel: {
      type: String,
      default() {
        return null;
      }
    },
    repeatCprValidationMessageRequired: {
      type: String,
      default() {
        return '';
      }
    },
    repeatCprValidationMessageNoMatch: {
      type: String,
      default() {
        return '';
      }
    },
    placeholder: {
      type: String,
      default() {
        return 'xxxxxx-xxxx';
      }
    },
    errors: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  mounted() {
    this.cprNo = this.modelValue ?? '';
  },
  unmounted() {},
  data() {
    return {
      fakeId: Math.round(Math.random() * 2000),
      cprNo: '',
      repeatedCprNo: '',
      firstFieldValid: false
    };
  },
  emits: ['update:modelValue'],
  methods: {
    addDash(value) {
      if (value != null && value.length >= 7) {
        return `${value.slice(0, 6)}-${value.slice(6)}`;
      }
      return value;
    },
    removeDash(value) {
      return (value ?? '').replace(/-/g, '');
    }
  },
  computed: {
    columnsClass() {
      return this.repeatCprNumber ? 'small-12 medium-6 columns' : 'small-12 columns';
    },
    repeatCprLabelComputed() {
      return this.repeatCprLabel == null ? 'Gentag ' + this.cprLabel : this.repeatCprLabel;
    },
    formattedCprNo: {
      get() {
        return this.addDash(this.modelValue);
      },
      set(value) {
        this.$emit('update:modelValue', this.removeDash(value));
      }
    },
    formattedRepeatedCprNo: {
      get() {
        return this.addDash(this.repeatedCprNo);
      },
      set(value) {
        this.repeatedCprNo = this.removeDash(value);
      }
    }
  },
  created() {
    defineRule('mod11', (value) => {
      var cpr = this.removeDash(value);
      if (cpr.length == 9) {
        cpr.value = '0' + cpr.value;
      }
      if (cpr.length == 10) {
        if (cpr.match(/[0-9]{10}/)) {
          var chk = 0;
          for (var i = 9; i > -1; i--) {
            chk += +cpr.charAt(i) * (i > 2 ? 10 - i : 4 - i);
          }
          if (chk % 11 == 0) {
            this.firstFieldValid = true;
            return true;
          }
          this.firstFieldValid = false;
          return false;
        }
      }
    });

    defineRule('cpr_required', (value, _, field) => {
      if (value === '' || value == null) {
        this.firstFieldValid = false;
        return messages.required(field);
      }
      this.firstFieldValid = true;
      return true;
    });

    defineRule('cpr_number_field', (value) => {
      var cpr = this.removeDash(value);
      if (cpr.length < 10 || !/^\d+$/.test(cpr)) {
        this.firstFieldValid = false;
        return false;
      }
      this.firstFieldValid = true;
      return true;
    });

    defineRule('repated_required', (value) => {
      if (value !== '') {
        return true;
      }
      return this.repeatCprValidationMessageRequired;
    });
    defineRule('cpr_number_repeat', (value, [targetValue], ctx) => {
      var cpr = ctx.form[targetValue];
      var cprRepeated = value;

      if (cpr === cprRepeated) {
        return true;
      }
      return this.repeatCprValidationMessageNoMatch;
    });
  }
};
</script>
